import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { CheckCheck, Inbox, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const TYPE_LABELS = { weekly_review: "Weekly report", journey_plan: "Route plan", final_assessment: "Final report" };
const TYPE_COLORS = {
  weekly_review: "bg-blue-50 text-blue-700 border-transparent",
  journey_plan: "bg-cyan-50 text-cyan-700 border-transparent",
  final_assessment: "bg-emerald-50 text-emerald-700 border-transparent",
};

export default function SubmittedReportsPage() {
  const { hasPermission } = useAuth();
  const canAck = hasPermission("reports:submissions");
  const [items, setItems] = useState([]);
  const [outstanding, setOutstanding] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/report-submissions");
      setItems(data.items);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canAck) return;
    api.get("/feedback/reminders/outstanding").then((r) => setOutstanding(r.data.items)).catch(() => {});
  }, [canAck]);

  const ack = async (id) => {
    try {
      await api.post(`/report-submissions/${id}/acknowledge`);
      toast.success("Marked as acknowledged");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const fmt = (v) => {
    try {
      return format(new Date(v), "dd MMM yyyy, HH:mm");
    } catch {
      return "";
    }
  };

  return (
    <div data-testid="submitted-reports-page" className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Submitted Reports</h1>
        <p className="text-sm text-slate-600 mt-1">
          {canAck
            ? "Reports sent by trainers to the Co-ordinator. The Co-ordinator and Manager can view and acknowledge them."
            : "Reports you have sent to the Co-ordinator."}
        </p>
      </div>

      {canAck && outstanding.length > 0 && (
        <Card data-testid="feedback-outstanding-card" className="border-amber-300">
          <CardContent className="p-4 space-y-2">
            <p className="text-sm font-semibold text-amber-800">Outstanding feedback reminders ({outstanding.length})</p>
            {outstanding.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`feedback-outstanding-row-${r.id}`}>
                <span className="font-medium text-slate-900">{r.child_name}</span>
                <span className="text-slate-600">
                  — {r.type === "parent_8weekly_due" ? "parent 8-weekly review" : "parent completion sign-off"}
                  {r.days_outstanding != null ? ` · ${r.days_outstanding} day${r.days_outstanding === 1 ? "" : "s"} outstanding` : ""}
                </span>
                {r.escalated && <Badge className="bg-red-50 text-red-700 border-transparent">Escalated</Badge>}
                <Link to={`/children/${r.child_id}`} className="ml-auto text-xs font-medium text-primary hover:underline" data-testid={`feedback-outstanding-open-${r.id}`}>
                  Open case
                </Link>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-600" data-testid="submitted-reports-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-16 text-center" data-testid="submitted-reports-empty">
          <Inbox className="mx-auto h-8 w-8 text-slate-300 mb-3" />
          <p className="text-sm text-slate-600">No reports submitted yet.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="submitted-reports-list">
          {items.map((s) => (
            <Card key={s.id} data-testid={`submitted-report-row-${s.id}`} className={s.status === "submitted" ? "border-l-4 border-l-primary" : "opacity-80"}>
              <CardContent className="p-4 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[220px]">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className={TYPE_COLORS[s.report_type] || "bg-slate-100 text-slate-600 border-transparent"} data-testid={`submitted-report-type-${s.id}`}>
                      {TYPE_LABELS[s.report_type] || s.report_type}
                    </Badge>
                    <p className="font-medium text-slate-900 text-sm">{s.title}</p>
                  </div>
                  <p className="text-sm text-slate-600 mt-0.5">
                    {s.child_name ? `${s.child_name} · ` : ""}sent by {s.submitted_by_name} · {fmt(s.created_at)}
                  </p>
                  {s.status === "acknowledged" && (
                    <p className="text-xs text-ukgreen mt-1" data-testid={`submitted-report-acked-${s.id}`}>
                      Acknowledged by {s.acknowledged_by_name} · {fmt(s.acknowledged_at)}
                    </p>
                  )}
                </div>
                {s.link && (
                  <Link to={s.link} className="text-xs font-medium text-primary hover:underline" data-testid={`submitted-report-open-${s.id}`}>
                    Open report
                  </Link>
                )}
                {canAck && s.status === "submitted" && (
                  <Button variant="outline" className="h-9 text-xs" data-testid={`submitted-ack-${s.id}`} onClick={() => ack(s.id)}>
                    <CheckCheck className="h-3.5 w-3.5 mr-1" /> Acknowledge
                  </Button>
                )}
                {s.status === "submitted" ? (
                  !canAck && <Badge className="bg-amber-50 text-amber-700 border-transparent">Awaiting review</Badge>
                ) : (
                  <Badge className="bg-ukgreen-light text-ukgreen border-transparent">Acknowledged</Badge>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
